import React, { useMemo, useState } from 'react';
import { Gruppe } from '../types';
import { ApiService } from '../api';
import Icon from './Icon';
import ActionSheet from './ActionSheet';
import { useToast } from './Toast';

interface GruppenListeProps {
  gruppen: Gruppe[];
  onGruppeSelect: (gruppe: Gruppe) => void;
  onRefresh: () => void;
}

const avatarColor = (name: string) => {
  const colors = ['#0a84ff', '#bf5af2', '#ff2d55', '#30d158', '#ff9f0a', '#63e6e2', '#5e5ce6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return colors[hash % colors.length];
};

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('') || '?';

const GruppenListe: React.FC<GruppenListeProps> = ({ gruppen, onGruppeSelect, onRefresh }) => {
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Gruppe | null>(null);
  const toast = useToast();

  const totals = useMemo(() => {
    const spieler = gruppen.reduce((a, g) => a + (g.spieler?.length ?? 0), 0);
    const sessions = gruppen.reduce((a, g) => a + (g.sessions?.length ?? 0), 0);
    return { spieler, sessions };
  }, [gruppen]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      await ApiService.createGruppe(newName.trim());
      setNewName('');
      onRefresh();
      toast.success('Gruppe erstellt');
    } catch (err) {
      console.error(err);
      toast.error('Gruppe konnte nicht erstellt werden');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await ApiService.deleteGruppe(deleteTarget.id);
      onRefresh();
      toast.success('Gruppe gelöscht');
    } catch (err) {
      console.error(err);
      toast.error('Gruppe konnte nicht gelöscht werden');
    }
  };

  return (
    <>
      <div className="bento-grid">
        <div className="bento glassy">
          <div className="bento-label">Gruppen</div>
          <div className="bento-value">{gruppen.length}</div>
          <div className="bento-sub">aktiv</div>
        </div>
        <div className="bento glassy">
          <div className="bento-label">Spieler</div>
          <div className="bento-value">{totals.spieler}</div>
          <div className="bento-sub">insgesamt</div>
        </div>
      </div>

      <form onSubmit={handleCreate} className="lg" style={{ padding: 12, marginBottom: 16 }}>
        <div className="input-row">
          <input
            className="input"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Neue Gruppe…"
            disabled={isCreating}
          />
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={isCreating || !newName.trim()}
            aria-label="Gruppe erstellen"
          >
            <Icon name="plus" size={18} />
            <span className="hide-sm">Erstellen</span>
          </button>
        </div>
      </form>

      {gruppen.length === 0 ? (
        <div className="empty-ios">
          <div className="empty-emoji">🎴</div>
          <div className="empty-title">Noch keine Gruppen</div>
          <div className="footnote">Leg oben deine erste Spielgruppe an.</div>
        </div>
      ) : (
        <>
          <div className="section-header">Deine Gruppen</div>
          <div className="ios-list">
            {gruppen.map((g) => (
              <div
                key={g.id}
                className="ios-row interactive"
                onClick={() => onGruppeSelect(g)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onGruppeSelect(g);
                  }
                }}
              >
                <div
                  className="row-icon"
                  style={{ background: avatarColor(g.name), width: 36, height: 36, borderRadius: 10, fontSize: 13, fontWeight: 700 }}
                >
                  {initials(g.name)}
                </div>
                <div className="row-main">
                  <div className="row-title">{g.name}</div>
                  <div className="row-subtitle">
                    {(g.spieler?.length ?? 0)} Spieler · {(g.sessions?.length ?? 0)} Sessions
                  </div>
                </div>
                <div className="row-aside">
                  <button
                    className="btn btn-sm btn-destructive-tinted btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(g);
                    }}
                    aria-label="Gruppe löschen"
                  >
                    <Icon name="trash" size={18} />
                  </button>
                  <Icon name="chevron-right" size={16} className="chevron" />
                </div>
              </div>
            ))}
          </div>
          <div className="section-footer">Tippe auf eine Gruppe, um Spieler zu verwalten und Sessions zu starten.</div>
        </>
      )}

      <ActionSheet
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Gruppe löschen?"
        message={deleteTarget ? `„${deleteTarget.name}“ wird mit allen Spielern und Sessions entfernt.` : undefined}
        actions={[
          { label: 'Löschen', style: 'destructive', onClick: handleDelete },
          { label: 'Abbrechen', style: 'cancel' },
        ]}
      />
    </>
  );
};

export default GruppenListe;
